import { Grid, styled, Modal, Box, CircularProgress } from '@mui/material'
import { memo, ReactNode } from 'react'
import SideNav from './SideNav/SideNav'
import { useAppSelector } from '../../redux/hook'
import ErrorBoundary from '../ErrorBoundaries/ErrorBoundary'
import { drawerWidth, RoutePaths } from '../../constants/constants'
import { withAuthenticationRequired } from '@auth0/auth0-react'
import TimeZoneDisplay from '../Common/TimezoneDisplay'
import { useLocation } from 'react-router-dom'

const Main = styled('main', {
    shouldForwardProp: (prop) => prop !== 'open',
})<{
    open?: boolean
}>(({ theme, open }) => ({
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `calc(${theme.spacing(8)} + 5px)`,
    width: `calc(100% - 75px)`,
    height: 'calc(100vh - 83px)',
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: `${drawerWidth + 10}px`,
        width: `calc(100% - ${drawerWidth + 20}px)`,
    }),
    border: '1px solid #959595',
    borderRadius: '8px',
    marginBottom: '20px',
    position: 'fixed',
    top: '80px',
    display: 'flex',
    flexDirection: 'column',
}))

// Memoize SideNav to prevent rerenders when routes change
const MemoizedSideNav = memo(SideNav)
MemoizedSideNav.displayName = 'MemoizedSideNav'

interface AuthenticatedLayoutProps {
    children: ReactNode
}

const AuthGuardPlaceholder = () => (
    <Modal open={true} style={{ backgroundColor: '#fff' }}>
        <Box
            sx={{
                margin: 'calc(50vh - 4vh) auto',
                width: '8vw',
                outline: 'none',
                border: 'none',
            }}
        >
            <CircularProgress size="8vw" />
        </Box>
    </Modal>
)

const AuthenticatedLayout = ({ children }: AuthenticatedLayoutProps) => {
    const open = useAppSelector((state) => state.nav.isOpen)
    const route = useLocation()

    const encodedUrl = route.pathname
    const routePathname = encodedUrl.replace(/%20/g, ' ')
    const timeZoneDisplayPaths = [
        RoutePaths.entity,
        RoutePaths.ccl,
        RoutePaths.facilityMonitoring,
        RoutePaths.analytics,
        RoutePaths.deviceManager,
        RoutePaths.uploadManager,
    ]
    const isTimeZoneVisible = timeZoneDisplayPaths.some((basePath) =>
        routePathname.includes(basePath),
    )
    return (
        <Grid>
            <Main open={open}>
                {isTimeZoneVisible ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: '14px',
                            top: '14px',
                        }}
                    >
                        <TimeZoneDisplay />
                    </Box>
                ) : null}
                <ErrorBoundary>{children}</ErrorBoundary>
            </Main>
            <MemoizedSideNav />
        </Grid>
    )
}

const AuthenticatedLayoutWithAuth = withAuthenticationRequired(
    AuthenticatedLayout,
    {
        onRedirecting: () => <AuthGuardPlaceholder />,
    },
)

AuthenticatedLayoutWithAuth.displayName = 'AuthenticatedLayoutWithAuth'

export default AuthenticatedLayoutWithAuth
